import { useState, useRef, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/ui/pagination";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { PaymentHistory } from "@/types/payment";
import { CustomerPaymentSheet } from "@/components/customer-payment-sheet";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, X, Printer, FileSpreadsheet, ChevronUp, ChevronDown, AlertTriangle, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface OverdueTableProps {
  customers: Customer[];
  products: Product[];
  paymentHistory?: PaymentHistory[];
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => void;
  onUpdateProduct: (product: Product) => void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "settings") => void;
}

export function OverdueTable({ customers, products, paymentHistory: initialPaymentHistory = [], onAddProduct, onDeleteProduct, onUpdateProduct, onNavigate }: OverdueTableProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchTerm, setSearchTerm] = useState("");
  
  // خوێندنەوەی فلتەرەکان لە localStorage
  const [jobFilter, setJobFilter] = useState<string | null>(() => {
    return localStorage.getItem('overdue-job-filter') || null;
  });
  const [currencyFilter, setCurrencyFilter] = useState<"all" | "IQD" | "USD">(() => {
    return (localStorage.getItem('overdue-currency-filter') as "all" | "IQD" | "USD") || "all";
  });
  const [daysFilter, setDaysFilter] = useState<"all" | "week" | "month" | "more">(() => {
    return (localStorage.getItem('overdue-days-filter') as "all" | "week" | "month" | "more") || "all";
  });
  const [codeSortOrder, setCodeSortOrder] = useState<"asc" | "desc" | null>(() => {
    const saved = localStorage.getItem('overdue-code-sort');
    return saved ? (saved as "asc" | "desc") : null;
  });
  
  const [jobPopoverOpen, setJobPopoverOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>(initialPaymentHistory);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // تۆمارکردنی فلتەرەکان لە localStorage کاتێک دەگۆڕێن
  useEffect(() => {
    if (jobFilter) {
      localStorage.setItem('overdue-job-filter', jobFilter);
    } else {
      localStorage.removeItem('overdue-job-filter');
    }
  }, [jobFilter]);

  useEffect(() => {
    localStorage.setItem('overdue-currency-filter', currencyFilter);
  }, [currencyFilter]);

  useEffect(() => {
    localStorage.setItem('overdue-days-filter', daysFilter);
  }, [daysFilter]);

  useEffect(() => {
    if (codeSortOrder) {
      localStorage.setItem('overdue-code-sort', codeSortOrder);
    } else {
      localStorage.removeItem('overdue-code-sort');
    }
  }, [codeSortOrder]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // API URL
  const getApiUrl = () => {
    return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
  };

  // خوێندنەوەی هەموو پارەدانەکان
  const fetchAllPayments = useCallback(async () => {
    setIsLoadingPayments(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/payments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  }, []);

  // خوێندنەوەی پارەدانەکان کاتێک کۆمپۆنێنت لۆد دەبێت
  useEffect(() => {
    fetchAllPayments();
  }, [fetchAllPayments]);

  const toggleExpanded = (productId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set<string>();
      if (!prev.has(productId)) {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "دواکەوتووەکان",
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 8mm;
      }
      @media print {
        * {
          box-sizing: border-box;
        }
        html {
          transform: scale(0.7);
          transform-origin: top right;
        }
        body {
          direction: rtl;
          font-family: 'Noto Kufi Arabic', sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          margin: 0;
          padding: 0;
        }
        .print-table {
          width: 142% !important;
          table-layout: auto;
          border-collapse: collapse;
        }
        .print-table th, .print-table td {
          border: 1px solid #ddd;
          padding: 6px 8px;
          text-align: right;
          white-space: nowrap;
        }
        .print-table th {
          background-color: #f5f5f5 !important;
          font-weight: bold;
        }
      }
    `,
  });

  const exportToExcel = () => {
    const data = sortedOverdue.map(item => ({
      "کۆد": item.customer?.customerCode || "",
      "ناوی کڕیار": item.customer?.fullName || "",
      "پیشە": item.customer?.job || "",
      "ناوی ئامێر": item.product.deviceName,
      "بڕی ماوە": item.product.remainingAmount,
      "دراو": item.product.currency === "IQD" ? "دینار" : "دۆلار",
      "کڕین": new Date(item.product.date).toLocaleDateString("ar-IQ"),
      "جار": item.product.installments || 0,
      "کۆتا پارەدان": item.lastPaymentDate ? item.lastPaymentDate.toLocaleDateString("ar-IQ") : "پارەدان نەکراوە",
      "شایستە": (item.product.installments || 0) === 0 ? "پارەدان نەکراوە" : item.dueDate.toLocaleDateString("ar-IQ"),
      "تێبینی": item.lastPaymentNote || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "دواکەوتووەکان");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `دواکەوتووەکان-${new Date().toLocaleDateString("ar-IQ")}.xlsx`);
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setProfileOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortField(null);
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortDirection === "asc"
      ? <ArrowUp className="h-3 w-3 text-primary" />
      : <ArrowDown className="h-3 w-3 text-primary" />;
  };

  // Calculate due date for each product
  // ئەگەر هیچ پارەدانێک نەکرابوو: بەرواری شایستە = بەرواری کڕین + 30 ڕۆژ
  // ئەگەر پارەدان کرابوو: بەرواری شایستە = کۆتا بەرواری پارەدان + 30 ڕۆژ
  const getNextDueDate = (product: Product): Date => {
    const lastPayment = getLastPaymentDate(product);
    
    if (lastPayment) {
      // ئەگەر پارەدان کرابوو، کۆی بەرواری کۆتا پارەدان بکە بە 30 ڕۆژ
      const nextDueDate = new Date(lastPayment);
      nextDueDate.setDate(nextDueDate.getDate() + 30);
      return nextDueDate;
    } else {
      // هیچ پارەدانێک نەکراوە - بەرواری شایستە = بەرواری کڕین + 30 ڕۆژ
      const purchaseDate = new Date(product.date);
      const nextDueDate = new Date(purchaseDate);
      nextDueDate.setDate(nextDueDate.getDate() + 30);
      return nextDueDate;
    }
  };

  const getDaysOverdue = (dueDate: Date): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getLastPaymentDate = (product: Product): Date | null => {
    if (!paymentHistory || paymentHistory.length === 0) return null;
    
    // دۆزینەوەی هەموو پارەدانەکانی ئەم کاڵایە
    const productPayments = paymentHistory.filter(p => p.productId === product.id);
    if (productPayments.length === 0) return null;
    
    // ڕیزکردنی بەپێی بەروار بۆ دۆزینەوەی کۆتا پارەدان
    const sortedPayments = [...productPayments].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // گەڕاندنەوەی کۆتا بەرواری پارەدان
    return new Date(sortedPayments[0].date);
  };

  // گەڕاندنەوەی کۆتا تێبینی پارەدان بۆ کاڵایەک
  const getLastPaymentNote = (productId: string): string | null => {
    if (!paymentHistory || paymentHistory.length === 0) return null;
    
    // دۆزینەوەی هەموو پارەدانەکانی ئەم کاڵایە
    const productPayments = paymentHistory.filter(p => p.productId === productId);
    if (productPayments.length === 0) return null;
    
    // ڕیزکردنی بەپێی ژمارەی جار (کۆتایی)
    const sortedPayments = [...productPayments].sort((a, b) => b.installmentNumber - a.installmentNumber);
    
    // گەڕاندنەوەی تێبینی کۆتا پارەدان
    return sortedPayments[0]?.note || null;
  };

  // Get overdue products with customer info
  const overdueProducts = products
    .filter(p => p.remainingAmount > 0)
    .map(product => {
      const customer = customers.find(c => c.id === product.customerId);
      const dueDate = getNextDueDate(product);
      const daysOverdue = getDaysOverdue(dueDate);
      const lastPaymentDate = getLastPaymentDate(product);
      const lastPaymentNote = getLastPaymentNote(product.id);
      return {
        product,
        customer,
        dueDate,
        daysOverdue,
        lastPaymentDate,
        lastPaymentNote,
      };
    })
    .filter(item => item.daysOverdue > 0 && item.customer);

  // Get unique jobs for filter
  const uniqueJobs = [...new Set(overdueProducts.map(item => item.customer?.job).filter(Boolean))] as string[];

  // Apply filters
  const filteredOverdue = overdueProducts.filter(item => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        item.customer?.fullName.toLowerCase().includes(search) ||
        item.customer?.customerCode.toString().includes(search) ||
        item.customer?.phone1.includes(search) ||
        item.product.deviceName.toLowerCase().includes(search) ||
        item.customer?.job?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Job filter
    if (jobFilter && item.customer?.job !== jobFilter) return false;

    // Currency filter
    if (currencyFilter !== "all" && item.product.currency !== currencyFilter) return false;

    // Days overdue filter
    if (daysFilter === "week" && item.daysOverdue > 7) return false;
    if (daysFilter === "month" && (item.daysOverdue <= 7 || item.daysOverdue > 30)) return false;
    if (daysFilter === "more" && item.daysOverdue <= 30) return false;

    return true;
  });

  // Sort overdue products
  const sortedOverdue = [...filteredOverdue].sort((a, b) => {
    // ئەگەر ڕیزکردنی کۆد چالاک بێت، لە پێشەوە ئەنجامی بدە
    if (codeSortOrder) {
      const aCode = Number(a.customer?.customerCode) || 0;
      const bCode = Number(b.customer?.customerCode) || 0;
      return codeSortOrder === "asc" ? aCode - bCode : bCode - aCode;
    }

    if (!sortField) return b.daysOverdue - a.daysOverdue; // Default: most overdue first

    let aValue: string | number = "";
    let bValue: string | number = "";

    switch (sortField) {
      case "code":
        aValue = a.customer?.customerCode || 0;
        bValue = b.customer?.customerCode || 0;
        break;
      case "name":
        aValue = a.customer?.fullName || "";
        bValue = b.customer?.fullName || "";
        break;
      case "job":
        aValue = a.customer?.job || "";
        bValue = b.customer?.job || "";
        break;
      case "device":
        aValue = a.product.deviceName;
        bValue = b.product.deviceName;
        break;
      case "remaining":
        aValue = a.product.remainingAmount;
        bValue = b.product.remainingAmount;
        break;
      case "dueDate":
        aValue = a.dueDate.getTime();
        bValue = b.dueDate.getTime();
        break;
      case "daysOverdue":
        aValue = a.daysOverdue;
        bValue = b.daysOverdue;
        break;
      case "purchaseDate":
        aValue = new Date(a.product.date).getTime();
        bValue = new Date(b.product.date).getTime();
        break;
      case "installments":
        aValue = a.product.installments || 0;
        bValue = b.product.installments || 0;
        break;
      case "lastPaymentDate":
        aValue = a.lastPaymentDate ? a.lastPaymentDate.getTime() : 0;
        bValue = b.lastPaymentDate ? b.lastPaymentDate.getTime() : 0;
        break;
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }

    return sortDirection === "asc"
      ? String(aValue).localeCompare(String(bValue), "ar")
      : String(bValue).localeCompare(String(aValue), "ar");
  });

  // Pagination
  const totalPages = Math.ceil(sortedOverdue.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOverdue = sortedOverdue.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (overdueProducts.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        هیچ دواکەوتنێک نییە
      </div>
    );
  }

  const hasActiveFilters = searchTerm || jobFilter || currencyFilter !== "all" || daysFilter !== "all" || codeSortOrder;

  const clearFilters = () => {
    setSearchTerm("");
    setJobFilter(null);
    setCurrencyFilter("all");
    setDaysFilter("all");
    setCodeSortOrder(null);
  };

  const toggleCodeSort = () => {
    if (!codeSortOrder) {
      setCodeSortOrder("asc");
    } else if (codeSortOrder === "asc") {
      setCodeSortOrder("desc");
    } else {
      setCodeSortOrder(null);
    }
  };

  return (
    <>
      {/* Desktop Search and Actions Bar */}
      <div className="hidden md:flex items-center justify-between gap-4 mb-4">
        <div className="relative w-72">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="گەڕان..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-9"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Code Sort Button */}
          <Button 
            variant={codeSortOrder ? "default" : "outline"} 
            size="sm"
            onClick={toggleCodeSort}
          >
            کۆد
            {codeSortOrder === "asc" && <ArrowUp className="h-3 w-3 mr-1" />}
            {codeSortOrder === "desc" && <ArrowDown className="h-3 w-3 mr-1" />}
            {!codeSortOrder && <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground" />}
          </Button>

          {/* Job Filter */}
          <Popover open={jobPopoverOpen} onOpenChange={setJobPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant={jobFilter ? "default" : "outline"} size="sm">
                {jobFilter || "پیشە"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-1" align="start" dir="rtl">
              <div
                className={cn(
                  "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                  !jobFilter && "bg-accent"
                )}
                onClick={() => { setJobFilter(null); setJobPopoverOpen(false); }}
              >
                هەموو
              </div>
              {uniqueJobs.map((job) => (
                <div
                  key={job}
                  className={cn(
                    "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                    jobFilter === job && "bg-accent"
                  )}
                  onClick={() => { setJobFilter(job); setJobPopoverOpen(false); }}
                >
                  {job}
                </div>
              ))}
            </PopoverContent>
          </Popover>

          {/* Currency Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={currencyFilter !== "all" ? "default" : "outline"} size="sm">
                {currencyFilter === "all" ? "دراو" : currencyFilter === "IQD" ? "دینار" : "دۆلار"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-1" align="start" dir="rtl">
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "all" && "bg-accent")} onClick={() => setCurrencyFilter("all")}>هەموو</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "IQD" && "bg-accent")} onClick={() => setCurrencyFilter("IQD")}>دینار</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "USD" && "bg-accent")} onClick={() => setCurrencyFilter("USD")}>دۆلار</div>
            </PopoverContent>
          </Popover>

          {/* Days Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant={daysFilter !== "all" ? "default" : "outline"} size="sm">
                {daysFilter === "all" ? "ماوە" : daysFilter === "week" ? "١-٧ ڕۆژ" : daysFilter === "month" ? "٨-٣٠ ڕۆژ" : "+٣٠ ڕۆژ"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-32 p-1" align="start" dir="rtl">
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "all" && "bg-accent")} onClick={() => setDaysFilter("all")}>هەموو</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "week" && "bg-accent")} onClick={() => setDaysFilter("week")}>١-٧ ڕۆژ</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "month" && "bg-accent")} onClick={() => setDaysFilter("month")}>٨-٣٠ ڕۆژ</div>
              <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "more" && "bg-accent")} onClick={() => setDaysFilter("more")}>+٣٠ ڕۆژ</div>
            </PopoverContent>
          </Popover>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" />
            </Button>
          )}

          <div className="h-6 w-px bg-border mx-1" />

          <Button variant="outline" size="sm" onClick={() => handlePrint()}>
            <Printer className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel}>
            <FileSpreadsheet className="h-4 w-4" />
          </Button>

          <Badge variant="secondary" className="mr-2">{sortedOverdue.length}</Badge>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="گەڕان..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-9"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Code Sort Button */}
            <Button 
              variant={codeSortOrder ? "default" : "outline"} 
              size="sm"
              onClick={toggleCodeSort}
            >
              کۆد
              {codeSortOrder === "asc" && <ArrowUp className="h-3 w-3 mr-1" />}
              {codeSortOrder === "desc" && <ArrowDown className="h-3 w-3 mr-1" />}
              {!codeSortOrder && <ArrowUpDown className="h-3 w-3 mr-1 text-muted-foreground" />}
            </Button>

            {/* Job Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={jobFilter ? "default" : "outline"} size="sm">
                  {jobFilter || "پیشە"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" align="start" dir="rtl">
                <div
                  className={cn(
                    "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                    !jobFilter && "bg-accent"
                  )}
                  onClick={() => setJobFilter(null)}
                >
                  هەموو
                </div>
                {uniqueJobs.map((job) => (
                  <div
                    key={job}
                    className={cn(
                      "px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                      jobFilter === job && "bg-accent"
                    )}
                    onClick={() => setJobFilter(job)}
                  >
                    {job}
                  </div>
                ))}
              </PopoverContent>
            </Popover>

            {/* Currency Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={currencyFilter !== "all" ? "default" : "outline"} size="sm">
                  {currencyFilter === "all" ? "دراو" : currencyFilter === "IQD" ? "دینار" : "دۆلار"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-32 p-1" align="start" dir="rtl">
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "all" && "bg-accent")} onClick={() => setCurrencyFilter("all")}>هەموو</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "IQD" && "bg-accent")} onClick={() => setCurrencyFilter("IQD")}>دینار</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", currencyFilter === "USD" && "bg-accent")} onClick={() => setCurrencyFilter("USD")}>دۆلار</div>
              </PopoverContent>
            </Popover>

            {/* Days Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={daysFilter !== "all" ? "default" : "outline"} size="sm">
                  {daysFilter === "all" ? "ماوە" : daysFilter === "week" ? "١-٧ ڕۆژ" : daysFilter === "month" ? "٨-٣٠ ڕۆژ" : "+٣٠ ڕۆژ"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-32 p-1" align="start" dir="rtl">
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "all" && "bg-accent")} onClick={() => setDaysFilter("all")}>هەموو</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "week" && "bg-accent")} onClick={() => setDaysFilter("week")}>١-٧ ڕۆژ</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "month" && "bg-accent")} onClick={() => setDaysFilter("month")}>٨-٣٠ ڕۆژ</div>
                <div className={cn("px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent", daysFilter === "more" && "bg-accent")} onClick={() => setDaysFilter("more")}>+٣٠ ڕۆژ</div>
              </PopoverContent>
            </Popover>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handlePrint()}>
              <Printer className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={exportToExcel}>
              <FileSpreadsheet className="h-4 w-4" />
            </Button>
            <Badge variant="secondary">{sortedOverdue.length}</Badge>
          </div>
        </div>
      </div>

      {sortedOverdue.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          هیچ ئەنجامێک نەدۆزرایەوە
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div ref={printRef} className="rounded-md border print:border-0 hidden md:block">
            {/* Print Header - تەنها لە کاتی چاپکردن دەردەکەوێت */}
            <div className="hidden print:flex print:flex-col print:items-center print:justify-center mb-4 text-center border-b pb-3">
              <h1 className="text-xl font-bold mb-1">لیستی دواکەوتووەکان</h1>
              <p className="text-sm text-muted-foreground">
                بەرواری چاپکردن: {new Date().toLocaleDateString("ku", { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <Table className="print-table">
              <TableHeader>
                <TableRow>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "code" ? "bg-muted/30" : ""}`} onClick={() => handleSort("code")}>
                    <div className="flex items-center gap-1">
                      کۆد
                      <SortIcon field="code" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "name" ? "bg-muted/30" : ""}`} onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-1">
                      ناوی کڕیار
                      <SortIcon field="name" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "job" ? "bg-muted/30" : ""}`} onClick={() => handleSort("job")}>
                    <div className="flex items-center gap-1">
                      پیشە
                      <SortIcon field="job" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "device" ? "bg-muted/30" : ""}`} onClick={() => handleSort("device")}>
                    <div className="flex items-center gap-1">
                      ناوی ئامێر
                      <SortIcon field="device" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "remaining" ? "bg-muted/30" : ""}`} onClick={() => handleSort("remaining")}>
                    <div className="flex items-center gap-1">
                      بڕی ماوە
                      <SortIcon field="remaining" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "purchaseDate" ? "bg-muted/30" : ""}`} onClick={() => handleSort("purchaseDate")}>
                    <div className="flex items-center gap-1">
                      کڕین
                      <SortIcon field="purchaseDate" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "installments" ? "bg-muted/30" : ""}`} onClick={() => handleSort("installments")}>
                    <div className="flex items-center gap-1">
                      جار
                      <SortIcon field="installments" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "lastPaymentDate" ? "bg-muted/30" : ""}`} onClick={() => handleSort("lastPaymentDate")}>
                    <div className="flex items-center gap-1">
                      کۆتا پارەدان
                      <SortIcon field="lastPaymentDate" />
                    </div>
                  </TableHead>
                  <TableHead className={`text-right cursor-pointer hover:bg-muted/50 ${sortField === "dueDate" ? "bg-muted/30" : ""}`} onClick={() => handleSort("dueDate")}>
                    <div className="flex items-center gap-1">
                      شایستە
                      <SortIcon field="dueDate" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    تێبینی
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedOverdue.map((item, index) => {
                  const globalIndex = startIndex + index;
                  return (
                  <TableRow
                    key={item.product.id}
                    className={`h-14 ${index % 2 === 1 ? 'bg-muted/50' : ''}`}
                  >
                    <TableCell className="py-2">
                      <Badge variant="outline">{item.customer?.customerCode}</Badge>
                    </TableCell>
                    <TableCell className="font-medium py-2">
                      <div className="flex flex-col gap-1">
                        <span>{item.customer?.fullName}</span>
                        {item.customer?.phone1 && (
                          <span className="text-sm font-normal text-right bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-900" dir="ltr">{item.customer.phone1}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      {item.customer?.job ? <Badge variant="secondary">{item.customer.job}</Badge> : "-"}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-col gap-1">
                        <span>{item.product.deviceName}</span>
                        {item.product.notes && (
                          <span className="text-sm font-normal text-right bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-900">
                            {item.product.notes}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1.5 justify-start">
                        <span className="font-semibold text-destructive" dir="ltr">
                          {item.product.remainingAmount.toLocaleString()}
                        </span>
                        <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">
                          {item.product.currency === "IQD" ? "د.ع" : "$"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <span dir="ltr">{new Date(item.product.date).toLocaleDateString("ar-IQ")}</span>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge variant="secondary">{item.product.installments || 0}</Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      {item.lastPaymentDate ? (
                        <span dir="ltr">{item.lastPaymentDate.toLocaleDateString("ar-IQ")}</span>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">پارەدان نەکراوە</Badge>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      {(item.product.installments || 0) === 0 ? (
                        <Badge variant="outline" className="text-destructive border-destructive">پارەدان نەکراوە</Badge>
                      ) : (
                        <span dir="ltr">{item.dueDate.toLocaleDateString("ar-IQ")}</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      {item.lastPaymentNote ? (
                        <span className="text-sm bg-blue-50 dark:bg-blue-950/30 px-2 py-1 rounded border border-blue-200 dark:border-blue-900 inline-block max-w-[200px] truncate" title={item.lastPaymentNote}>
                          {item.lastPaymentNote}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={sortedOverdue.length}
              itemsPerPage={itemsPerPage}
            />
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paginatedOverdue.map((item, index) => {
              const globalIndex = startIndex + index;
              const isExpanded = expandedItems.has(item.product.id);
              const isEven = index % 2 === 1;
              return (
                <div
                  key={item.product.id}
                  className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}
                >
                  <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(item.product.id)}>
                    {/* Main Row */}
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm truncate">{item.customer?.fullName}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 h-4">
                            {item.customer?.customerCode}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {item.product.deviceName}
                        </div>
                        {item.product.notes && (
                          <div className="text-sm font-normal text-right bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-900 mt-1 truncate">
                            {item.product.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors cursor-pointer">
                              <Phone className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="text-right w-56">
                              <div dir="rtl">
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                  ژمارەکانی پەیوەندی
                                </div>
                                <DropdownMenuSeparator />
                                <div className="bg-muted/40 p-1 rounded-md space-y-1 mx-1 mb-1">
                                  {item.customer?.phone1 && (
                                    <DropdownMenuItem asChild className="bg-background shadow-sm border focus:bg-accent">
                                      <a
                                        href={`tel:${item.customer.phone1}`}
                                        className="flex items-center gap-2 cursor-pointer w-full"
                                      >
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                          <Phone className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-[10px] text-muted-foreground">مۆبایل ١</div>
                                          <div className="font-medium text-sm" dir="ltr">{item.customer.phone1}</div>
                                        </div>
                                      </a>
                                    </DropdownMenuItem>
                                  )}
                                  {item.customer?.phone2 && (
                                    <DropdownMenuItem asChild className="bg-background shadow-sm border focus:bg-accent">
                                      <a
                                        href={`tel:${item.customer.phone2}`}
                                        className="flex items-center gap-2 cursor-pointer w-full"
                                      >
                                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                          <Phone className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-[10px] text-muted-foreground">مۆبایل ٢</div>
                                          <div className="font-medium text-sm" dir="ltr">{item.customer.phone2}</div>
                                        </div>
                                      </a>
                                    </DropdownMenuItem>
                                  )}
                                  {item.customer?.guarantorPhone && (
                                    <DropdownMenuItem asChild className="bg-background shadow-sm border focus:bg-accent">
                                      <a
                                        href={`tel:${item.customer.guarantorPhone}`}
                                        className="flex items-center gap-2 cursor-pointer w-full"
                                      >
                                        <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                                          <Phone className="h-4 w-4 text-amber-600" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="text-[10px] text-muted-foreground">کەفیل</div>
                                          <div className="font-medium text-sm" dir="ltr">{item.customer.guarantorPhone}</div>
                                        </div>
                                      </a>
                                    </DropdownMenuItem>
                                  )}
                                </div>
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        <button
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(item.product.id);
                          }}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    {/* Mini Table */}
                    <div className="grid grid-cols-2 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2 w-full" dir="rtl">
                      <div className="text-center">
                        <div className="text-muted-foreground text-[10px]">ماوە</div>
                        <div className="font-semibold text-destructive flex items-center justify-center gap-1">
                          {item.product.currency === "IQD" ? (
                            <>
                              <span dir="ltr">{item.product.remainingAmount.toLocaleString()}</span>
                              <span className="text-[10px]">د.ع</span>
                            </>
                          ) : (
                            <span dir="ltr">${item.product.remainingAmount.toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-muted-foreground text-[10px]">شایستە</div>
                        <div className="font-semibold text-[10px]">
                          {(item.product.installments || 0) === 0 ? (
                            <span className="text-destructive">نەکراوە</span>
                          ) : (
                            <span dir="ltr">{item.dueDate.toLocaleDateString("ar-IQ")}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                      <div className="space-y-2 pt-3">
                        {/* زانیاری کڕیار */}
                        {item.customer?.phone1 && (
                          <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                            <span className="text-muted-foreground text-xs">مۆبایل</span>
                            <span className="font-medium text-sm" dir="ltr">
                              {item.customer.phone1}
                            </span>
                          </div>
                        )}
                        {item.customer?.job && (
                          <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                            <span className="text-muted-foreground text-xs">پیشە</span>
                            <Badge variant="secondary" className="text-xs">{item.customer.job}</Badge>
                          </div>
                        )}

                        {/* زانیاری کاڵا */}
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                          <span className="text-muted-foreground text-xs">بەرواری کڕین</span>
                          <span className="font-medium text-sm" dir="ltr">{new Date(item.product.date).toLocaleDateString("ar-IQ")}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                          <span className="text-muted-foreground text-xs">نرخی فرۆشتن</span>
                          <span className="font-medium text-sm" dir={item.product.currency === "IQD" ? "rtl" : "ltr"}>
                            {item.product.currency === "IQD" ? (
                              <>{item.product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></>
                            ) : (
                              <><span className="text-[10px] text-muted-foreground">$ </span>{item.product.salePrice.toLocaleString()}</>
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                          <span className="text-muted-foreground text-xs">بڕی وەرگیراو</span>
                          <span className="font-medium text-sm text-green-600" dir={item.product.currency === "IQD" ? "rtl" : "ltr"}>
                            {item.product.currency === "IQD" ? (
                              <>{item.product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></>
                            ) : (
                              <><span className="text-[10px]">$ </span>{item.product.paidAmount.toLocaleString()}</>
                            )}
                          </span>
                        </div>

                        {/* زانیاری پارەدان */}
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                          <span className="text-muted-foreground text-xs">ژمارەی جار</span>
                          <Badge variant="secondary" className="text-xs">{item.product.installments || 0}</Badge>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                          <span className="text-muted-foreground text-xs">کۆتا پارەدان</span>
                          <span className="font-medium text-sm" dir="ltr">
                            {item.lastPaymentDate ? item.lastPaymentDate.toLocaleDateString("ar-IQ") : "پارەدان نەکراوە"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1.5">
                          <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                          <span className="font-medium text-sm text-blue-600" dir={item.product.currency === "IQD" ? "rtl" : "ltr"}>
                            {item.product.currency === "IQD" ? (
                              <>{item.product.monthlyInstallment.toLocaleString()} <span className="text-[10px]">د.ع</span></>
                            ) : (
                              <><span className="text-[10px]">$ </span>{item.product.monthlyInstallment.toLocaleString()}</>
                            )}
                          </span>
                        </div>

                        {/* تێبینی کۆتا پارەدان */}
                        {item.lastPaymentNote && (
                          <div className="pt-2 mt-1 border-t">
                            <span className="text-muted-foreground text-xs block mb-1">تێبینی کۆتا پارەدان</span>
                            <p className="text-sm bg-blue-50 dark:bg-blue-950/30 rounded-lg p-2 text-blue-600 border border-blue-200 dark:border-blue-900">{item.lastPaymentNote}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalItems={sortedOverdue.length}
              itemsPerPage={itemsPerPage}
            />
          </div>
        </>
      )}

      <CustomerPaymentSheet
        customer={selectedCustomer}
        products={products}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onAddProduct={onAddProduct}
        onDeleteProduct={onDeleteProduct}
        onUpdateProduct={onUpdateProduct}
        onNavigate={onNavigate}
        hideAddProduct={true}
      />
    </>
  );
}
